import { Chip } from '@mui/material'
import { getGridSingleSelectOperators, type GridColDef } from '@mui/x-data-grid'

import type { Scout, ScoutStatus } from '../../types.ts'

import { ScoutActionButtons } from './ScoutActionButtons'

// Custom filter operators for status - only "is any of"
const statusFilterOperators = getGridSingleSelectOperators().filter(
  operator => operator.value === 'isAnyOf'
)

export const getColumns = ({
  isMobile,
  canDelete,
}: {
  isMobile: boolean
  canDelete: boolean
}): GridColDef<Scout>[] => [
  {
    field: 'firstName',
    headerName: 'First Name',
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: isMobile ? 1 : 2,
    minWidth: isMobile ? 150 : 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone',
    width: 150,
    sortable: false,
    filterable: false,
    renderCell: ({ value }) => (value as string) || '-',
  },
  {
    field: 'address',
    headerName: 'Address',
    flex: 1.5,
    minWidth: 200,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => {
      const streetPart = row.street || ''
      const cityPart = [row.postalcode, row.city].filter(Boolean).join(' ')
      const addressParts = [streetPart, cityPart].filter(Boolean)
      return addressParts.length > 0 ? addressParts.join(', ') : '-'
    },
  },
  {
    field: 'emailVerification',
    headerName: 'Email Verified',
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: ({ value }) => (
      <Chip
        label={value ? 'Verified' : 'Not Verified'}
        color={value ? 'success' : 'warning'}
        size="small"
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    sortable: false,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [
      'new',
      'Aktiv',
      'On Hold',
      'Inaktiv',
      'Partnerschaft beendet (Sonstiges)',
      'Technisches Onboarding',
    ],
    filterOperators: statusFilterOperators,
    renderCell: ({ value }) => {
      if (!value) return '-'

      const getStatusColor = (status: ScoutStatus) => {
        switch (status) {
          case 'new':
            return 'default' as const
          case 'Aktiv':
            return 'success' as const
          case 'Inaktiv':
            return 'error' as const
          case 'On Hold':
            return 'warning' as const
          case 'Partnerschaft beendet (Sonstiges)':
            return 'error' as const
          case 'Technisches Onboarding':
            return 'info' as const
          default:
            return 'default' as const
        }
      }

      return (
        <Chip label={value as string} color={getStatusColor(value as ScoutStatus)} size="small" />
      )
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    filterable: false,
    width: 124,
    renderCell: ({ row }) => <ScoutActionButtons record={row} canDelete={canDelete} />,
  },
]
